export type PaymentProvider = "tng" | "maybank" | "gxbank";
export interface PaymentReceipt {
  provider: PaymentProvider;
  amount: string;
  merchant: string;
  reference: string;
  transactionDate: string;
  dateFromCapture: boolean;
}

function review(message: string): never {
  throw new Error(`${message} Nothing was saved. Record this payment manually.`);
}

function calendarDate(year: number, month: number, day: number) {
  const value = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const date = new Date(`${value}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) review("The receipt date is invalid.");
  return value;
}

// Shared with the Settings preview. OCR runs on the phone, without an external AI service.
export function parsePaymentReceipt(text: string, capturedAt: string, chosenProvider: "auto" | PaymentProvider = "auto"): PaymentReceipt {
  if (!text.trim() || text.length > 12_000) review("The screenshot text is empty or too long.");
  const capture = new Date(capturedAt);
  if (!Number.isFinite(capture.getTime())) review("The capture time is invalid.");
  const lines = text.normalize("NFKC").replace(/[’‘]/g, "'").split(/\r?\n/).map((line) => line.trim().replace(/\s+/g, " ")).filter(Boolean);
  const receipt = lines.join("\n");
  if (/\b(failed|unsuccessful|pending|declined|cancelled|canceled|refunded|received|gagal|tertunda|diterima)\b/i.test(receipt)) review("This is not a completed outgoing payment.");
  if (!/\b(payment|transaction|pembayaran)\s+(successful|success|completed|berjaya)\b|\bsuccessful\s+payment\b|^successful$/im.test(receipt)) review("A payment-success message was not found.");
  if (!/\b(payment|paid|merchant|pembayaran|qr)\b/i.test(receipt)) review("This is not a supported payment receipt.");
  if (/\b(transfer|reload|top[ -]?up|cash\s*out|withdrawal)\b/i.test(receipt)) review("Transfers and wallet top-ups need manual recording.");

  // Only use branding above the success message. A recipient's bank is not the payer's bank.
  const successIndex = lines.findIndex((line) => /successful|completed|berjaya|payment success/i.test(line));
  const heading = lines.slice(0, successIndex + 1).join(" ");
  const tng = /\btng\b|touch\s*'?n\s*go/i.test(heading);
  const maybank = /\bmaybank\b|\bmae\b/i.test(heading);
  const gxbank = /\bgx\s*bank\b|\bgxbank\b/i.test(heading);
  const detected = [tng, maybank, gxbank].filter(Boolean).length === 1 ? tng ? "tng" : maybank ? "maybank" : "gxbank" : undefined;
  if (chosenProvider !== "auto" && detected && detected !== chosenProvider) review("The selected payment app conflicts with the receipt.");
  const provider = chosenProvider === "auto" ? detected : chosenProvider;
  if (!provider) review("The paying app is unclear. Choose TNG, Maybank or GXBank in the Shortcut.");

  const amounts: string[] = [];
  const moneyPattern = /\b(?:RM|MYR)\s*((?:\d{1,3}(?:,\d{3})+|\d+)\.\d{2})(?![\d.,])/gi;
  for (let index = 0; index < lines.length; index++) {
    const line = lines[index]!;
    const previous = lines[index - 1] ?? "";
    if (/balance|cashback|reward|fee|discount|baki/i.test(line) || (/balance|cashback|reward|fee|discount|baki/i.test(previous) && !/\b(?:RM|MYR)\s*\d/i.test(previous))) continue;
    // Some OCR layouts put RM on its own line.
    for (const match of `${line}${/^(RM|MYR)$/i.test(line) ? ` ${lines[index + 1] ?? ""}` : ""}`.matchAll(moneyPattern)) amounts.push(match[1]!.replace(/,/g, ""));
  }
  const distinct = [...new Set(amounts)];
  if (distinct.length !== 1 || !/^\d{1,12}\.\d{2}$/.test(distinct[0] ?? "") || Number(distinct[0]) <= 0) review("One clear RM payment amount could not be found.");

  const referenceLabel = /^(?:(?:duitnow\s+)?(?:transaction|payment)\s*(?:id|no\.?|number|reference(?:\s*(?:id|no\.?|number))?)|(?:duitnow\s+)?ref(?:erence)?\.?\s*(?:id|no\.?|number)?)\s*[:#-]?\s*(.*)$/i;
  const references: string[] = [];
  for (let index = 0; index < lines.length; index++) {
    const match = lines[index]!.match(referenceLabel);
    if (!match) continue;
    const candidate = match[1] || lines[index + 1] || "";
    if (/^\d{1,2}\s+[a-z]{3,9}\s+20\d{2}$/i.test(candidate) || /^\d{1,4}[/-]\d{1,2}[/-]\d{2,4}$/.test(candidate)) continue;
    const value = candidate.replace(/\s/g, "").toUpperCase();
    if (/^[A-Z0-9-]{6,80}$/.test(value) && /\d/.test(value)) references.push(value);
  }
  if (!references.length) review("A transaction reference is needed to prevent duplicate records.");
  // Use all labelled identifiers in a stable order, independent of capture time or status-bar text.
  const reference = [...new Set(references)].sort().join("/");

  let merchant = "";
  for (let index = 0; index < lines.length; index++) {
    const match = lines[index]!.match(/^(?:paid to|payment to|pay to|merchant(?: name)?|recipient(?: name)?|to|kepada)(?:\s*[:：-]\s*|\s+|$)(.*)$/i);
    if (match) { merchant = match[1] || lines[index + 1] || ""; break; }
  }
  if (!merchant || merchant.length > 100 || !/[\p{L}]/u.test(merchant) || /\b(RM|MYR|reference|transaction|amount|successful|balance)\b/i.test(merchant)) review("The merchant name could not be identified.");

  const dates: string[] = [];
  for (const match of receipt.matchAll(/\b(20\d{2})-(\d{2})-(\d{2})\b/g)) dates.push(calendarDate(Number(match[1]), Number(match[2]), Number(match[3])));
  for (const match of receipt.matchAll(/\b(\d{1,2})[/-](\d{1,2})[/-](20\d{2})\b/g)) dates.push(calendarDate(Number(match[3]), Number(match[2]), Number(match[1])));
  const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
  for (const match of receipt.matchAll(/\b(\d{1,2})\s+(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s*,?\s*(20\d{2})\b/gi)) dates.push(calendarDate(Number(match[3]), months.indexOf(match[2]!.slice(0, 3).toLowerCase()) + 1, Number(match[1])));
  const uniqueDates = [...new Set(dates)];
  if (uniqueDates.length > 1) review("The receipt contains conflicting dates.");
  if (!uniqueDates.length && /\bdate\b|\btarikh\b/i.test(receipt)) review("The printed receipt date could not be read.");
  // Malaysia has no daylight-saving offset. Capture time is a fallback only for a live success screen.
  const captureDate = new Date(capture.getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return { provider, amount: distinct[0]!, merchant, reference, transactionDate: uniqueDates[0] ?? captureDate, dateFromCapture: !uniqueDates.length };
}
